// SPDX-License-Identifier: AGPL-3.0-or-later

import AppStorage from '@app/features/platform/state/PersistentStorage';
import {
	type AdvanceLastReadScrollEdge,
	shouldCaptureVisitAnchor,
	shouldReappearBannerOnLiveMessage,
} from '@app/features/read_state/utils/UnreadCatchUp';
import {makeSyncedField} from '@app/features/user/state/SyncedField';
import {UnreadCatchUpSettingsSchema} from '@fluxer/schema/src/gen/fluxer/user/preferences/v1/preferences_pb';
import {makeAutoObservable} from 'mobx';

const LEGACY_STORAGE_KEY = 'UnreadCatchUpSettings';

type LegacyUnreadCatchUpSettings = {
	advanceLastReadOnScroll?: boolean;
	advanceLastReadScrollEdge?: string;
	markReadToMessage?: boolean;
	hideNewBannerOnCatchUp?: boolean;
	clearDividerAtBottom?: boolean;
	clearOnIdleAtBottom?: boolean;
	clearOnLeaveAtBottom?: boolean;
};

function normalizeScrollEdge(value: string | undefined): AdvanceLastReadScrollEdge {
	if (value === 'bottom') {
		return 'bottom';
	}
	return 'top';
}

export function readLegacyUnreadCatchUpSettings(
	storage: Pick<typeof AppStorage, 'getItem'> = AppStorage,
): LegacyUnreadCatchUpSettings | null {
	try {
		const raw = storage.getItem(LEGACY_STORAGE_KEY);
		if (raw == null) {
			return null;
		}
		return JSON.parse(raw) as LegacyUnreadCatchUpSettings;
	} catch {
		return null;
	}
}

export function applyLegacyUnreadCatchUpSettings(
	settings: Pick<
		UnreadCatchUpSettings,
		| 'advanceLastReadOnScroll'
		| 'advanceLastReadScrollEdge'
		| 'markReadToMessage'
		| 'hideNewBannerOnCatchUp'
		| 'clearDividerAtBottom'
		| 'clearOnIdleAtBottom'
		| 'clearOnLeaveAtBottom'
	>,
	storage: Pick<typeof AppStorage, 'getItem' | 'removeItem'> = AppStorage,
): boolean {
	const legacy = readLegacyUnreadCatchUpSettings(storage);
	if (legacy == null) {
		return false;
	}
	if (legacy.advanceLastReadOnScroll === true) {
		settings.advanceLastReadOnScroll = true;
	}
	if (legacy.advanceLastReadOnScroll === false) {
		settings.advanceLastReadOnScroll = false;
	}
	if (legacy.advanceLastReadScrollEdge != null) {
		settings.advanceLastReadScrollEdge = normalizeScrollEdge(legacy.advanceLastReadScrollEdge);
	}
	if (legacy.markReadToMessage === true) {
		settings.markReadToMessage = true;
	}
	if (legacy.markReadToMessage === false) {
		settings.markReadToMessage = false;
	}
	if (legacy.hideNewBannerOnCatchUp === true) {
		settings.hideNewBannerOnCatchUp = true;
	}
	if (legacy.clearDividerAtBottom === true) {
		settings.clearDividerAtBottom = true;
	}
	if (legacy.clearDividerAtBottom === false) {
		settings.clearDividerAtBottom = false;
	}
	if (legacy.clearOnIdleAtBottom === true) {
		settings.clearOnIdleAtBottom = true;
	}
	if (legacy.clearOnLeaveAtBottom === true) {
		settings.clearOnLeaveAtBottom = true;
	}
	try {
		storage.removeItem(LEGACY_STORAGE_KEY);
	} catch {}
	return true;
}

class UnreadCatchUpSettings {
	advanceLastReadOnScroll = true;
	advanceLastReadScrollEdge: AdvanceLastReadScrollEdge = 'top';
	markReadToMessage = true;
	hideNewBannerOnCatchUp = false;
	clearDividerAtBottom = true;
	clearOnIdleAtBottom = false;
	clearOnLeaveAtBottom = false;
	bannerHiddenForVisit = new Set<string>();
	visitAnchorUnreadId = new Map<string, string | null>();
	visitStartAckMessageId = new Map<string, string | null>();
	visitEnteredAt = new Map<string, number>();

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		applyLegacyUnreadCatchUpSettings(this);
		void makeSyncedField(this, {
			field: 'unreadCatchUp',
			schema: UnreadCatchUpSettingsSchema,
			persist: [
				'advanceLastReadOnScroll',
				'advanceLastReadScrollEdge',
				'markReadToMessage',
				'hideNewBannerOnCatchUp',
				'clearDividerAtBottom',
				'clearOnIdleAtBottom',
				'clearOnLeaveAtBottom',
			],
			toMessage: (settings) => ({
				advanceLastReadOnScroll: settings.advanceLastReadOnScroll ? undefined : false,
				advanceLastReadScrollEdge: settings.advanceLastReadScrollEdge,
				markReadToMessage: settings.markReadToMessage ? undefined : false,
				hideNewBannerOnCatchUp: settings.hideNewBannerOnCatchUp ? true : undefined,
				clearDividerAtBottom: settings.clearDividerAtBottom ? undefined : false,
				clearOnIdleAtBottom: settings.clearOnIdleAtBottom ? true : undefined,
				clearOnLeaveAtBottom: settings.clearOnLeaveAtBottom ? true : undefined,
			}),
			applyMessage: (settings, message) => {
				settings.advanceLastReadOnScroll = message.advanceLastReadOnScroll ?? true;
				settings.advanceLastReadScrollEdge = normalizeScrollEdge(message.advanceLastReadScrollEdge);
				settings.markReadToMessage = message.markReadToMessage ?? true;
				settings.hideNewBannerOnCatchUp = message.hideNewBannerOnCatchUp ?? false;
				settings.clearDividerAtBottom = message.clearDividerAtBottom ?? true;
				settings.clearOnIdleAtBottom = message.clearOnIdleAtBottom ?? false;
				settings.clearOnLeaveAtBottom = message.clearOnLeaveAtBottom ?? false;
			},
			mergeRemote: (local, incoming) => ({
				advanceLastReadOnScroll:
					incoming.advanceLastReadOnScroll ?? (local.advanceLastReadOnScroll === false ? false : undefined),
				advanceLastReadScrollEdge:
					incoming.advanceLastReadScrollEdge ?? (local.advanceLastReadScrollEdge === 'bottom' ? 'bottom' : undefined),
				markReadToMessage: incoming.markReadToMessage ?? (local.markReadToMessage === false ? false : undefined),
				hideNewBannerOnCatchUp: incoming.hideNewBannerOnCatchUp ?? (local.hideNewBannerOnCatchUp ? true : undefined),
				clearDividerAtBottom:
					incoming.clearDividerAtBottom ?? (local.clearDividerAtBottom === false ? false : undefined),
				clearOnIdleAtBottom: incoming.clearOnIdleAtBottom ?? (local.clearOnIdleAtBottom ? true : undefined),
				clearOnLeaveAtBottom: incoming.clearOnLeaveAtBottom ?? (local.clearOnLeaveAtBottom ? true : undefined),
			}),
		});
	}

	setAdvanceLastReadOnScroll(value: boolean): void {
		this.advanceLastReadOnScroll = value;
	}

	setAdvanceLastReadScrollEdge(value: AdvanceLastReadScrollEdge): void {
		this.advanceLastReadScrollEdge = value;
	}

	setMarkReadToMessage(value: boolean): void {
		this.markReadToMessage = value;
	}

	setHideNewBannerOnCatchUp(value: boolean): void {
		this.hideNewBannerOnCatchUp = value;
	}

	setClearDividerAtBottom(value: boolean): void {
		this.clearDividerAtBottom = value;
	}

	setClearOnIdleAtBottom(value: boolean): void {
		this.clearOnIdleAtBottom = value;
	}

	setClearOnLeaveAtBottom(value: boolean): void {
		this.clearOnLeaveAtBottom = value;
	}

	enterChannelVisit(channelId: string, originalUnreadId?: string | null, ackMessageId?: string | null): void {
		this.bannerHiddenForVisit.delete(channelId);
		this.visitAnchorUnreadId.set(channelId, originalUnreadId ?? null);
		this.visitStartAckMessageId.set(channelId, ackMessageId ?? null);
		this.visitEnteredAt.set(channelId, Date.now());
	}

	captureVisitAnchor(channelId: string, originalUnreadId: string | null, currentAckId?: string | null): void {
		if (
			!shouldCaptureVisitAnchor({
				originalUnreadId,
				existingAnchor: this.visitAnchorUnreadId.get(channelId),
				visitStartAckId: this.visitStartAckMessageId.get(channelId),
				currentAckId: currentAckId ?? null,
			})
		) {
			return;
		}
		this.visitAnchorUnreadId.set(channelId, originalUnreadId);
	}

	leaveChannelVisit(channelId: string): void {
		this.bannerHiddenForVisit.delete(channelId);
		this.visitAnchorUnreadId.delete(channelId);
		this.visitStartAckMessageId.delete(channelId);
		this.visitEnteredAt.delete(channelId);
	}

	getVisitDurationMs(channelId: string, now = Date.now()): number {
		const enteredAt = this.visitEnteredAt.get(channelId);
		if (enteredAt == null) {
			return 0;
		}
		return Math.max(0, now - enteredAt);
	}

	hideBannerForVisit(channelId: string): void {
		this.bannerHiddenForVisit.add(channelId);
	}

	reappearBannerForManualUnread(channelId: string): void {
		this.bannerHiddenForVisit.delete(channelId);
	}

	reappearBannerForLiveMessage(channelId: string): void {
		if (
			shouldReappearBannerOnLiveMessage({
				hideNewBannerOnCatchUp: this.hideNewBannerOnCatchUp,
				bannerHiddenForVisit: this.isBannerHiddenForVisit(channelId),
			})
		) {
			this.bannerHiddenForVisit.delete(channelId);
		}
	}

	isBannerHiddenForVisit(channelId: string): boolean {
		return this.bannerHiddenForVisit.has(channelId);
	}

	getVisitAnchorUnreadId(channelId: string): string | null {
		return this.visitAnchorUnreadId.get(channelId) ?? null;
	}
}

export default new UnreadCatchUpSettings();
